import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Maximize2,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Search,
  RotateCcw,
  Trash2,
  CheckCircle2,
  AlertCircle,
  Eye,
  FileText,
  Mail,
  Users,
  FileBarChart2,
} from 'lucide-react';
import { restoreCandidate, softDeleteCandidate, startCandidateBGC } from '../../services/user/api';
import logo from '../../assets/images/Trueface.png';

/**
 * Returns raw content without automatically turning path mentions into link buttons.
 * Links will only render when explicitly formatted in Markdown by the LLM.
 */
const formatContentLinks = (text) => text;

/**
 * Parses hidden pagination metadata JSON from the LLM/formatter response,
 * with a fallback parser for markdown tables to ensure pagination is always available.
 */
const parseCandidatePagination = (text) => {
  if (!text) return null;
  const marker = '<!-- CANDIDATE_PAGINATION:';
  const startIdx = text.indexOf(marker);
  if (startIdx !== -1) {
    const jsonStart = text.indexOf('{', startIdx + marker.length);
    if (jsonStart !== -1) {
      const endIdx = text.lastIndexOf('-->');
      if (endIdx !== -1 && endIdx > jsonStart) {
        const jsonEnd = text.lastIndexOf('}', endIdx);
        if (jsonEnd !== -1 && jsonEnd >= jsonStart) {
          const jsonStr = text.substring(jsonStart, jsonEnd + 1);
          try {
            return JSON.parse(jsonStr);
          } catch (e) {
            // Fall through to markdown table parsing
          }
        }
      }
    }
  }

  // Fallback: Check if response contains a Markdown table
  const lines = text.split('\n').map((l) => l.trim()).filter((l) => l.startsWith('|') && l.endsWith('|'));
  if (lines.length >= 3) {
    const rawHeaders = lines[0].split('|').map((c) => c.trim()).filter(Boolean);
    const dataLines = lines.slice(1).filter((l) => !l.includes('---'));
    if (dataLines.length > 0) {
      const cleanHeader = rawHeaders.map((h) => h.toLowerCase());

      // Guard: Never hijack metric, KPI, workload, or summary tables
      const isMetricSummary = cleanHeader.some((h) =>
        h.includes('metric') ||
        h.includes('kpi') ||
        h.includes('operational status') ||
        h.includes('parameter') ||
        h.includes('description') ||
        h.includes('measure')
      );
      if (isMetricSummary) {
        return null;
      }

      // Guard: Must have at least one entity identity column (name, email, or explicit entity id)
      const hasNameCol = cleanHeader.some((h) => h.includes('name'));
      const hasEmailCol = cleanHeader.some((h) => h.includes('email'));
      const hasExplicitEntityIdCol = cleanHeader.some((h) =>
        h.includes('candidate id') ||
        h.includes('client id') ||
        h.includes('employee id') ||
        h.includes('agent id')
      );
      if (!hasNameCol && !hasEmailCol && !hasExplicitEntityIdCol) {
        return null;
      }

      const isCandidate = cleanHeader.some((h) => h.includes('candidate') || h.includes('bgc status'));
      const isClient = !isCandidate && cleanHeader.some((h) => h.includes('client') || h.includes('company'));
      const isEmployee = !isCandidate && !isClient && cleanHeader.some((h) => h.includes('agent') || h.includes('employee'));

      const items = dataLines.map((line, idx) => {
        const cells = line.split('|').map((c) => c.trim().replace(/^\*\*|\*\*$/g, '').replace(/^`|`$/g, '')).slice(1, -1);
        const obj = { id: String(idx + 1) };
        rawHeaders.forEach((h, hIdx) => {
          const val = cells[hIdx] || '—';
          const hl = h.toLowerCase();
          if (hl.includes('id')) {
            obj.id = val;
            obj.candidateId = val;
          } else if (hl.includes('name') && !hl.includes('company') && !hl.includes('client')) {
            obj.name = val;
            obj.fullName = val;
          } else if (hl.includes('client')) {
            obj.clientName = val;
            if (!obj.name) obj.name = val;
          } else if (hl.includes('company')) {
            obj.company = val;
            obj.companyName = val;
          } else if (hl.includes('email')) {
            obj.email = val;
          } else if (hl.includes('status')) {
            obj.status = val;
            obj.bgcStatus = val;
            obj.clientStatus = val;
          } else if (hl.includes('phone') || hl.includes('contact') || hl.includes('mobile')) {
            obj.phone = val;
            obj.mobile = val;
          }
        });
        return obj;
      });

      const tableType = isClient ? 'admin_clients' : (isEmployee ? 'admin_employees' : 'admin_candidates');
      return {
        page: 1,
        pageSize: 10,
        totalPages: Math.max(1, Math.ceil(items.length / 10)),
        total: items.length,
        title: isClient ? 'clients' : (isEmployee ? 'employees' : 'candidates'),
        tableType: tableType,
        module: 'admin',
        items: items,
      };
    }
  }

  return null;
};

const getEmployeeStatusClass = (status) => {
  if (!status) return 'assigned-status-default';
  const normalized = String(status).toLowerCase();
  if (
    normalized.includes('pending') ||
    normalized.includes('expired') ||
    normalized.includes('work in progress') ||
    normalized.includes('work_in_progress') ||
    normalized.includes('in progress')
  ) {
    return 'assigned-status-orange';
  }
  if (normalized.includes('verified')) {
    return 'assigned-status-blue';
  }
  if (normalized.includes('completed')) {
    return 'assigned-status-green';
  }
  return 'assigned-status-default';
};

const getBGCButtonLabel = (status) => {
  const s = String(status || '').toUpperCase();
  if (s.includes('WORK_IN_PROGRESS') || s.includes('IN_PROGRESS') || s.includes('WORK IN PROGRESS')) return 'In Progress';
  if (s.includes('COMPLETED')) return 'Completed';
  if (s.includes('VERIFIED')) return 'View';
  if (s.includes('PENDING')) return 'Pending';
  if (s.includes('YET_TO_START') || s.includes('NOT_STARTED')) return 'Start BGC';
  return 'Start BGC';
};

const isBGCButtonDisabled = (status) => {
  const s = String(status || '').toUpperCase();
  return s.includes('COMPLETED') || s.includes('PENDING');
};

/**
 * Builds candidate markdown table for a given slice of candidate items.
 * Renders Quick BGV (8 columns) or Assigned Candidates List (6 columns) for employee module,
 * Pending Submission table (8 columns with Created Time & Actions) or Client Candidates table (10 columns) for client module.
 */
const buildCandidateTable = (items, total, startIndex = 0, title = 'candidates', opts = {}) => {
  const isAdminEmployee = Boolean(
    opts?.isAdminEmployee ||
    opts?.tableType === 'admin_employees' ||
    (opts?.module === 'admin' && (String(title).toLowerCase().includes('employee') || String(title).toLowerCase().includes('agent')))
  );
  const isAdminClient = !isAdminEmployee && Boolean(
    opts?.isAdminClient ||
    opts?.tableType === 'admin_clients' ||
    (opts?.module === 'admin' && (String(title).toLowerCase().includes('client') || String(title).toLowerCase().includes('company')))
  );
  const isAdminCandidate = !isAdminEmployee && !isAdminClient && Boolean(
    opts?.isAdminCandidate ||
    opts?.isAdmin ||
    opts?.tableType === 'admin_candidates' ||
    opts?.module === 'admin' ||
    (typeof window !== 'undefined' && (window.location.pathname.startsWith('/admin') || localStorage.getItem('user_role') === 'ADMIN') && !opts?.module?.includes('client'))
  );
  const isEmployee = !isAdminCandidate && !isAdminEmployee && !isAdminClient && (typeof opts === 'boolean' ? opts : Boolean(opts?.isEmployee));
  const isQuickBgv = !isAdminCandidate && !isAdminEmployee && !isAdminClient && Boolean(opts?.isQuickBgv || String(title).toLowerCase().includes('quick bgv'));
  const isPendingCaseSubmission = !isAdminCandidate && !isAdminEmployee && !isAdminClient && Boolean(
    opts?.isPendingCaseSubmission ||
    String(title).toLowerCase().includes('pending case')
  );
  const isLoginExpired = !isAdminCandidate && !isAdminEmployee && !isAdminClient && Boolean(
    !isPendingCaseSubmission && (
      opts?.isLoginExpired ||
      String(title).toLowerCase().includes('login expired') ||
      String(title).toLowerCase().includes('expired login')
    )
  );
  const isPendingSubmission = !isAdminCandidate && !isAdminEmployee && !isAdminClient && Boolean(
    !isPendingCaseSubmission && !isLoginExpired && (
      opts?.isPendingSubmission ||
      String(title).toLowerCase().includes('pending submission') ||
      String(title).toLowerCase().includes('not submitted') ||
      String(title).toLowerCase().includes('non submit')
    )
  );
  const isSubmitted = !isAdminCandidate && !isAdminEmployee && !isAdminClient && Boolean(
    !isPendingCaseSubmission && !isPendingSubmission && !isLoginExpired && (
      opts?.isSubmitted ||
      opts?.tableType === 'submitted' ||
      String(title).toLowerCase().includes('submitted for verification') ||
      String(title).toLowerCase().includes('submitted verification') ||
      String(title).toLowerCase().includes('submitted candidate') ||
      String(title).toLowerCase().includes('submitted')
    )
  );
  const isBgvCompleted = !isAdminCandidate && !isAdminEmployee && !isAdminClient && Boolean(
    !isPendingCaseSubmission && !isPendingSubmission && !isLoginExpired && !isSubmitted && (
      opts?.isBgvCompleted ||
      String(title).toLowerCase().includes('bgv completed') ||
      String(title).toLowerCase().includes('completed candidates')
    )
  );
  const isReadyForBgc = !isAdminCandidate && !isAdminEmployee && !isAdminClient && Boolean(
    !isPendingCaseSubmission && !isPendingSubmission && !isLoginExpired && !isSubmitted && !isBgvCompleted && (
      opts?.isReadyForBgc ||
      opts?.tableType === 'ready_for_bgc' ||
      String(title).toLowerCase().includes('ready for bgc') ||
      String(title).toLowerCase().includes('ready to bgc') ||
      String(title).toLowerCase().includes('ready bgc')
    )
  );

  let md = `There are **${total}** ${title}:\n\n`;

  if (isAdminEmployee) {
    md += '| Agent ID | Name | Email | Contact Number | Status | Actions |\n';
    md += '| :--- | :--- | :--- | :--- | :--- | :--- |\n';
    items.forEach((e) => {
      const id = e.id || e.userid || e.userId || '—';
      const name = e.name || e.username || e.fullName || '—';
      const email = e.email || '—';
      const phone = e.phone || e.contact || e.phonenumber || '—';
      const status = e.status || e.userStatus || 'Active';
      const act = `[Performance](/admin/employee-performance/${id})`;
      md += `| \`${id}\` | **${name}** | ${email} | ${phone} | **${status}** | ${act} |\n`;
    });
    return md;
  }

  if (isAdminClient) {
    md += '| Client Name | Company | Email | Contact Number | Status | Actions |\n';
    md += '| :--- | :--- | :--- | :--- | :--- | :--- |\n';
    items.forEach((c) => {
      const id = c.id || c.clientId || '';
      const name = c.name || c.username || c.clientName || '—';
      const company = c.company || c.companyName || c.companyname || '—';
      const email = c.email || c.emailaddress || c.companyEmail || '—';
      const phone = c.phone || c.phoneNumber || c.phonenumber || '—';
      const status = c.status || c.clientStatus || c.userStatus || 'ACTIVE';
      const act = `[Candidates](/admin/client/candidates/${id}) · [Usage](/admin/reports/client-usage/${id})`;
      md += `| **${name}** | ${company} | ${email} | ${phone} | **${status}** | ${act} |\n`;
    });
    return md;
  }

  if (isAdminCandidate) {
    md += '| Candidate ID | Name | Email | BGC Status | Client Name | Actions |\n';
    md += '| :--- | :--- | :--- | :--- | :--- | :--- |\n';
    items.forEach((c) => {
      const cid = c.candidateId || c.id || '—';
      const name = c.fullName || c.name || c.candidateName || '—';
      const email = c.email || c.candidateEmail || c.emailId || c.userEmail || '—';
      const bgcStatus = c.bgcStatus || c.status || 'VERIFIED';
      const clientName = c.clientName || c.company || c.companyName || '—';
      const act = `[View Details](/admin/candidate-details/${cid})`;
      md += `| \`${cid}\` | **${name}** | ${email} | **${bgcStatus}** | ${clientName} | ${act} |\n`;
    });
    return md;
  }

  if (isPendingCaseSubmission) {
    md += '| Candidate ID | Name | Email | DOB | Created Date | Created Time | Action | View Details |\n';
    md += '| :--- | :--- | :--- | :--- | :--- | :--- | :--- | :--- |\n';
    items.forEach((c) => {
      const cid = c.id || '—';
      const name = c.name || '—';
      const email = c.email || '—';
      const dob = c.dob || '—';
      const cDate = c.createdDate || '---';
      const cTime = c.createdTime || '---';
      const act = `[Request for BGC](/client/pending-case-submission)`;
      const view = `[View Details](/candidate-details/${cid})`;
      md += `| \`${cid}\` | **${name}** | ${email} | ${dob} | ${cDate} | ${cTime} | ${act} | ${view} |\n`;
    });
    return md;
  }

  if (isLoginExpired) {
    md += '| Candidate ID | Name | Email | Mobile Number | DOB | Created Date | Created Time |\n';
    md += '| :--- | :--- | :--- | :--- | :--- | :--- | :--- |\n';
    items.forEach((c) => {
      const cid = c.id || '—';
      const name = c.name || '—';
      const email = c.email || '—';
      const mobile = c.mobileNumber || c.mobile || '—';
      const dob = c.dob || '—';
      const cDate = c.createdDate || '---';
      const cTime = c.createdTime || '---';
      md += `| \`${cid}\` | **${name}** | ${email} | ${mobile} | ${dob} | ${cDate} | ${cTime} |\n`;
    });
    return md;
  }

  if (isQuickBgv) {
    md += '| Candidate ID | Full Name | Email | Mobile | DOB | Created By | Verification | Status |\n';
    md += '| :--- | :--- | :--- | :--- | :--- | :--- | :--- | :--- |\n';
    items.forEach((c) => {
      const cid = c.id || '—';
      const name = c.name || '—';
      const email = c.email || '—';
      const mobile = c.mobile || c.mobileNumber || '—';
      const dob = c.dob || '—';
      const createdBy = c.createdBy || '—';
      const verif = c.verification || 'Start BGC';
      const st = String(c.status || c.bgcStatus || 'IN PROGRESS').replace('_', ' ');
      md += `| \`${cid}\` | **${name}** | ${email} | ${mobile} | ${dob} | ${createdBy} | **${verif}** | **${st}** |\n`;
    });
    return md;
  }

  if (isPendingSubmission) {
    md += '| Candidate ID | Name | Email | Mobile Number | DOB | Created Date | Created Time | Actions |\n';
    md += '| :--- | :--- | :--- | :--- | :--- | :--- | :--- | :--- |\n';
    items.forEach((c) => {
      const cid = c.id || '—';
      const name = c.name || '—';
      const email = c.email || '—';
      const mobile = c.mobileNumber || c.mobile || '—';
      const dob = c.dob || '—';
      const cDate = c.createdDate || '---';
      const cTime = c.createdTime || '---';
      const isExp = c.loginExpired || c.actions === 'Login Expired';
      const stPill = isExp ? 'Login Expired' : 'Active';
      const act = `[Restore](/client/login-expired) · **${stPill}** · [Delete](/client/pending-submission)`;
      md += `| \`${cid}\` | **${name}** | ${email} | ${mobile} | ${dob} | ${cDate} | ${cTime} | ${act} |\n`;
    });
    return md;
  }

  if (isSubmitted) {
    md += '| Candidate ID | Name | Email | Mobile Number | DOB | Created Date | Created Time | Status | Actions |\n';
    md += '| :--- | :--- | :--- | :--- | :--- | :--- | :--- | :--- | :--- |\n';
    items.forEach((c) => {
      const cid = c.id || '—';
      const name = c.name || '—';
      const email = c.email || '—';
      const mobile = c.mobileNumber || c.mobile || '—';
      const dob = c.dob || '—';
      const cDate = c.createdDate || '---';
      const cTime = c.createdTime || '---';
      const rawStatus = c.status || c.bgcStatus || '—';
      const act = `[View Details](/candidate-details/${cid})`;
      md += `| \`${cid}\` | **${name}** | ${email} | ${mobile} | ${dob} | ${cDate} | ${cTime} | **${rawStatus}** | ${act} |\n`;
    });
    return md;
  }

  if (isBgvCompleted) {
    md += '| Candidate ID | Name | Email | Mobile | DOB | Created Date | BGV Completed Date | Action |\n';
    md += '| :--- | :--- | :--- | :--- | :--- | :--- | :--- | :--- |\n';
    items.forEach((c) => {
      const cid = c.id || '—';
      const name = c.name || '—';
      const email = c.email || '—';
      const mobile = c.mobile || c.mobileNumber || '—';
      const dob = c.dob || '—';
      const cDate = c.createdDate || '---';
      const compDate = c.completedDate || c.bgvCompletedDate || c.reportDateOnlyString || '---';
      const act = `[Report](/client/finalreport/${cid})`;
      md += `| \`${cid}\` | **${name}** | ${email} | ${mobile} | ${dob} | ${cDate} | ${compDate} | ${act} |\n`;
    });
    return md;
  }

  if (isReadyForBgc) {
    md += '| Candidate ID | Name | Email | Mobile Number | DOB | Created Date | Created Time | Status | Actions |\n';
    md += '| :--- | :--- | :--- | :--- | :--- | :--- | :--- | :--- | :--- |\n';
    items.forEach((c) => {
      const cid = c.candidateId || c.id || '—';
      const name = c.fullName || c.name || c.candidateName || '—';
      const email = c.email || c.candidateEmail || '—';
      const mobile = c.mobileNumber || c.mobile || '—';
      const dob = c.dob || c.dateOfBirth || '—';
      const cDate = c.createdDate || c.caseInitiatedDateOnly || '---';
      const cTime = c.createdTime || c.caseInitiatedTimeOnly || '---';
      const rawStatus = c.status || c.bgcStatus || 'READY_FOR_BGC';
      const act = `[View Details](/candidate-details/${cid})`;
      md += `| \`${cid}\` | **${name}** | ${email} | ${mobile} | ${dob} | ${cDate} | ${cTime} | **${rawStatus}** | ${act} |\n`;
    });
    return md;
  }

  if (isEmployee) {
    md += '| Candidate ID | Name | Email | Status | Verification | Actions |\n';
    md += '| :--- | :--- | :--- | :--- | :--- | :--- |\n';
    items.forEach((c) => {
      const cid = c.id || '—';
      const rawStatus = c.status || c.bgcStatus || '—';
      const verif = c.verification || (String(rawStatus).toUpperCase() === 'COMPLETED' ? 'Completed' : (String(rawStatus).toUpperCase() === 'NOT_STARTED' ? 'Start BGC' : 'In Progress'));
      const act = `[View](/candidate-details/${cid})`;
      md += `| \`${cid}\` | **${c.name}** | ${c.email || '—'} | **${rawStatus}** | **${verif}** | ${act} |\n`;
    });
    return md;
  }

  const hasExtended = items.some(
    (c) => c.mobile || c.mobileNumber || c.dob || c.createdDate || c.bgcStartDate || c.completedDate || c.actions
  );
  if (hasExtended) {
    md += '| Candidate ID | Name | Email | Mobile | DOB | Created Date | BGC Start Date | Completed Date | Status | Actions |\n';
    md += '| :--- | :--- | :--- | :--- | :--- | :--- | :--- | :--- | :--- | :--- |\n';
    items.forEach((c) => {
      const mob = c.mobile || c.mobileNumber || '—';
      const dob = c.dob || '—';
      const cDate = c.createdDate || '---';
      const sDate = c.bgcStartDate || '---';
      const compDate = c.completedDate || '---';
      const rawStatus = c.bgcStatus || c.status || '—';
      const cid = c.id || '—';
      let act = `[View](/candidate-details/${cid})`;
      if (String(rawStatus).toUpperCase() === 'COMPLETED') {
        act += ` · [Report](/client/finalreport/${cid})`;
      }
      md += `| \`${cid}\` | **${c.name}** | ${c.email || '—'} | ${mob} | ${dob} | ${cDate} | ${sDate} | ${compDate} | **${rawStatus}** | ${act} |\n`;
    });
  } else {
    md += '| Candidate ID | Name | Email / Client | Status |\n';
    md += '| :--- | :--- | :--- | :--- |\n';
    items.forEach((c) => {
      md += `| \`${c.id}\` | **${c.name}** | ${c.email || '—'} | **${c.status || '—'}** |\n`;
    });
  }
  return md;
};

/**
 * Counts the number of data points, list items, or table rows in a bot response.
 * The expand icon is enabled ONLY when the response exceeds 5 points (> 5).
 */
const countResponsePoints = (text) => {
  if (!text) return 0;

  const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);

  // 1. Check for Markdown Table rows (excluding header & delimiter)
  const tableLines = lines.filter((l) => l.startsWith('|') && l.endsWith('|'));
  if (tableLines.length >= 3) {
    const dataRows = tableLines.filter((l) => !l.includes('---'));
    const rowCount = Math.max(0, dataRows.length - 1);
    if (rowCount > 0) return rowCount;
  }

  // 2. Check for List Items (numbered, bulleted, step/item headers)
  const listItemRegex = /^(?:\d+[.)]|[-*+•]|#{1,6}\s*\d+[.)]|\*\*\d+[.)]\*\*|\*\*(?:Step|Point|Candidate|Item|\d+)\s*\d*:?\*\*)\s+/i;
  const listItems = lines.filter((l) => listItemRegex.test(l));
  if (listItems.length > 0) {
    return listItems.length;
  }

  // 3. Fallback check for structured lines with key-values
  if (lines.length > 5) {
    const structuredLines = lines.filter((l) => l.includes(':') || l.includes('Candidate') || l.includes('Status'));
    if (structuredLines.length > 5) {
      return structuredLines.length;
    }
  }

  return 0;
};

/**
 * Renders a single chat message bubble.
 * Bot messages are rendered as Markdown with clickable internal & external link buttons.
 * Includes a per-query expand button that opens a wide view panel to the LEFT of the chatbot container,
 * enabled ONLY if the response exceeds 5 points (> 5).
 * Supports interactive Previous / Next candidate pagination buttons matching Pic 3.
 */
const MessageBubble = ({ role, content, isWelcome, guardrailTriggered, onSend }) => {
  const isUser = role === 'user';
  const [modalOpen, setModalOpen] = useState(false);

  // ── Candidate Pagination Extraction ────────────────────────────────────────
  const paginationMeta = !isUser ? parseCandidatePagination(content) : null;
  const [currentPage, setCurrentPage] = useState(paginationMeta?.page || 1);
  const [candidateItems, setCandidateItems] = useState(paginationMeta?.items || []);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('createdDate');
  const [sortAsc, setSortAsc] = useState(false);
  const [actionToast, setActionToast] = useState(null);

  const handleNavigate = (path) => {
    setModalOpen(false);
    window.history.pushState({}, '', path);
    window.dispatchEvent(new Event('popstate'));
    if (window.location.pathname !== path) {
      setTimeout(() => {
        if (window.location.pathname !== path) {
          window.location.pathname = path;
        }
      }, 100);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortAsc((prev) => !prev);
    } else {
      setSortField(field);
      setSortAsc(true);
    }
  };

  const renderSortArrow = (field) => {
    const isActive = sortField === field;
    return (
      <span className={`chatbot-sort-arrows ${isActive ? 'active' : ''}`}>
        {!isActive ? '▲▼' : sortAsc ? '▲' : '▼'}
      </span>
    );
  };

  useEffect(() => {
    if (paginationMeta?.items) {
      setCandidateItems(paginationMeta.items);
    }
    if (paginationMeta?.page) {
      setCurrentPage(paginationMeta.page);
    }
  }, [content, paginationMeta?.page]);

  // Real-time search filter across all candidate fields (matches portal search)
  const filteredCandidates = useMemo(() => {
    if (!searchTerm.trim()) return candidateItems;
    const term = searchTerm.toLowerCase();
    return candidateItems.filter((c) => {
      return (
        (c.id && String(c.id).toLowerCase().includes(term)) ||
        (c.candidateId && String(c.candidateId).toLowerCase().includes(term)) ||
        (c.name && String(c.name).toLowerCase().includes(term)) ||
        (c.fullName && String(c.fullName).toLowerCase().includes(term)) ||
        (c.username && String(c.username).toLowerCase().includes(term)) ||
        (c.email && String(c.email).toLowerCase().includes(term)) ||
        (c.company && String(c.company).toLowerCase().includes(term)) ||
        (c.companyName && String(c.companyName).toLowerCase().includes(term)) ||
        (c.clientName && String(c.clientName).toLowerCase().includes(term)) ||
        ((c.mobile || c.mobileNumber || c.phone || c.phoneNumber) && String(c.mobile || c.mobileNumber || c.phone || c.phoneNumber).toLowerCase().includes(term)) ||
        (c.dob && String(c.dob).toLowerCase().includes(term)) ||
        (c.createdDate && String(c.createdDate).toLowerCase().includes(term)) ||
        (c.createdTime && String(c.createdTime).toLowerCase().includes(term)) ||
        (c.bgcStartDate && String(c.bgcStartDate).toLowerCase().includes(term)) ||
        (c.completedDate && String(c.completedDate).toLowerCase().includes(term)) ||
        ((c.bgcStatus || c.status || c.clientStatus) && String(c.bgcStatus || c.status || c.clientStatus).toLowerCase().includes(term)) ||
        (c.actions && String(c.actions).toLowerCase().includes(term))
      );
    });
  }, [candidateItems, searchTerm]);

  const isAdminEmployeeTable = useMemo(() => {
    if (paginationMeta?.module === 'client' || (typeof window !== 'undefined' && window.location.pathname.startsWith('/client'))) return false;
    if (paginationMeta?.tableType === 'admin_employees') return true;
    const title = String(paginationMeta?.title || '').toLowerCase();
    if (paginationMeta?.module === 'admin' && (title.includes('employee') || title.includes('agent'))) {
      return true;
    }
    if (typeof window !== 'undefined' && (window.location.pathname.includes('/admin/active-employee') || window.location.pathname.includes('/admin/inactive-employee'))) {
      return true;
    }
    return false;
  }, [paginationMeta]);

  const isAdminClientTable = useMemo(() => {
    if (isAdminEmployeeTable) return false;
    if (paginationMeta?.tableType === 'admin_candidates' || paginationMeta?.tableType === 'admin_employees') return false;
    if (paginationMeta?.module === 'client' || (typeof window !== 'undefined' && window.location.pathname.startsWith('/client'))) return false;
    if (paginationMeta?.tableType === 'admin_clients') return true;
    const title = String(paginationMeta?.title || '').toLowerCase();
    if (paginationMeta?.module === 'admin' && (title.includes('client') || title.includes('company'))) {
      return true;
    }
    if (typeof window !== 'undefined') {
      const path = window.location.pathname;
      const role = String(localStorage.getItem('user_role') || localStorage.getItem('role') || '').toUpperCase();
      if (role === 'ADMIN' || path.startsWith('/admin')) {
        if (title.includes('client') || title.includes('company')) {
          return true;
        }
        if (path.startsWith('/admin/client')) {
          if (title.includes('candidate') || title.includes('bgv') || title.includes('completed') || title.includes('verified')) return false;
          return true;
        }
      }
    }
    return false;
  }, [paginationMeta, isAdminEmployeeTable]);

  const isAdminCandidateTable = useMemo(() => {
    if (isAdminClientTable || isAdminEmployeeTable) return false;
    if (paginationMeta?.tableType === 'admin_candidates') return true;
    if (paginationMeta?.module === 'client' || (typeof window !== 'undefined' && window.location.pathname.startsWith('/client'))) return false;
    if (paginationMeta?.module === 'admin') {
      const title = String(paginationMeta?.title || '').toLowerCase();
      if (!title.includes('client') && !title.includes('company') && !title.includes('employee') && !title.includes('agent')) {
        return true;
      }
    }
    if (typeof window !== 'undefined') {
      const path = window.location.pathname;
      const role = String(localStorage.getItem('user_role') || localStorage.getItem('role') || '').toUpperCase();
      if (role === 'ADMIN' || path.startsWith('/admin')) {
        const title = String(paginationMeta?.title || '').toLowerCase();
        if (!title.includes('client') && !title.includes('company') && !title.includes('employee') && !title.includes('agent')) {
          return true;
        }
      }
    }
    return false;
  }, [paginationMeta, isAdminClientTable, isAdminEmployeeTable]);

  const isPendingCaseSubmissionTable = useMemo(() => {
    if (isAdminClientTable || isAdminCandidateTable || isAdminEmployeeTable) return false;
    if (paginationMeta?.tableType === 'pending_case_submission') return true;
    const title = String(paginationMeta?.title || '').toLowerCase();
    if (title.includes('pending case')) return true;
    if (typeof window !== 'undefined' && window.location.pathname.includes('/client/pending-case-submission')) {
      return true;
    }
    return false;
  }, [paginationMeta, isAdminClientTable, isAdminCandidateTable, isAdminEmployeeTable]);

  const isLoginExpiredTable = useMemo(() => {
    if (isAdminClientTable || isAdminCandidateTable || isAdminEmployeeTable || isPendingCaseSubmissionTable) return false;
    if (paginationMeta?.tableType === 'login_expired') return true;
    const title = String(paginationMeta?.title || '').toLowerCase();
    if (title.includes('login expired') || title.includes('expired login')) return true;
    if (typeof window !== 'undefined' && window.location.pathname.includes('/client/login-expired')) {
      return true;
    }
    return false;
  }, [paginationMeta, isAdminClientTable, isAdminCandidateTable, isAdminEmployeeTable, isPendingCaseSubmissionTable]);

  const isPendingSubmissionTable = useMemo(() => {
    if (isAdminClientTable || isAdminCandidateTable || isAdminEmployeeTable || isPendingCaseSubmissionTable || isLoginExpiredTable) return false;
    if (paginationMeta?.tableType === 'pending_submission') return true;
    const title = String(paginationMeta?.title || '').toLowerCase();
    if (
      title.includes('pending submission') ||
      title.includes('non submit') ||
      title.includes('not submitted')
    ) {
      return true;
    }
    if (typeof window !== 'undefined' && window.location.pathname.includes('/client/pending-submission')) {
      return true;
    }
    return false;
  }, [paginationMeta, isAdminClientTable, isAdminCandidateTable, isAdminEmployeeTable, isPendingCaseSubmissionTable, isLoginExpiredTable]);

  const isQuickBgvTable = useMemo(() => {
    if (isAdminClientTable || isAdminCandidateTable || isAdminEmployeeTable) return false;
    if (paginationMeta?.tableType === 'quick_bgv') return true;
    const title = String(paginationMeta?.title || '').toLowerCase();
    if (title.includes('quick bgv') || title.includes('quick candidate')) return true;
    if (typeof window !== 'undefined' && window.location.pathname.includes('/employee/quickbgv')) {
      return true;
    }
    return false;
  }, [paginationMeta, isAdminClientTable, isAdminCandidateTable, isAdminEmployeeTable]);

  const isSubmittedTable = useMemo(() => {
    if (isAdminClientTable || isAdminCandidateTable || isAdminEmployeeTable || isPendingCaseSubmissionTable || isPendingSubmissionTable || isLoginExpiredTable) return false;
    if (paginationMeta?.tableType === 'submitted' || paginationMeta?.tableType === 'submitted_for_verification') return true;
    const title = String(paginationMeta?.title || '').toLowerCase();
    if (
      title.includes('submitted for verification') ||
      title.includes('submitted verification') ||
      title.includes('submitted candidate') ||
      title.includes('submitted')
    ) {
      return true;
    }
    if (typeof window !== 'undefined' && window.location.pathname.includes('/client/case/submitted')) {
      return true;
    }
    return false;
  }, [paginationMeta, isAdminClientTable, isAdminCandidateTable, isAdminEmployeeTable, isPendingCaseSubmissionTable, isPendingSubmissionTable, isLoginExpiredTable]);

  const isBgvCompletedTable = useMemo(() => {
    if (isAdminClientTable || isAdminCandidateTable || isAdminEmployeeTable || isPendingCaseSubmissionTable || isPendingSubmissionTable || isLoginExpiredTable || isSubmittedTable) return false;
    if (paginationMeta?.tableType === 'bgv_completed') return true;
    const title = String(paginationMeta?.title || '').toLowerCase();
    if (title.includes('bgv completed') || title.includes('completed candidates')) return true;
    if (typeof window !== 'undefined' && (window.location.pathname.includes('/client/bgv-completed') || window.location.pathname.includes('completed'))) {
      return true;
    }
    return false;
  }, [paginationMeta, isAdminClientTable, isAdminCandidateTable, isAdminEmployeeTable, isPendingCaseSubmissionTable, isPendingSubmissionTable, isLoginExpiredTable, isSubmittedTable]);

  const isReadyForBgcTable = useMemo(() => {
    if (isAdminClientTable || isAdminCandidateTable || isAdminEmployeeTable || isPendingCaseSubmissionTable || isPendingSubmissionTable || isLoginExpiredTable || isSubmittedTable || isBgvCompletedTable) return false;
    if (paginationMeta?.tableType === 'ready_for_bgc') return true;
    const title = String(paginationMeta?.title || '').toLowerCase();
    if (title.includes('ready for bgc') || title.includes('ready to bgc') || title.includes('ready bgc')) return true;
    if (typeof window !== 'undefined' && window.location.pathname.includes('/client/case/ready')) {
      return true;
    }
    return false;
  }, [paginationMeta, isAdminClientTable, isAdminCandidateTable, isAdminEmployeeTable, isPendingCaseSubmissionTable, isPendingSubmissionTable, isLoginExpiredTable, isSubmittedTable, isBgvCompletedTable]);

  const isEmployeeTable = useMemo(() => {
    if (
      isAdminClientTable ||
      isAdminCandidateTable ||
      isAdminEmployeeTable ||
      paginationMeta?.module === 'admin' ||
      paginationMeta?.tableType?.startsWith('admin_') ||
      paginationMeta?.module === 'client' ||
      isQuickBgvTable ||
      isPendingSubmissionTable ||
      isPendingCaseSubmissionTable ||
      isSubmittedTable ||
      isLoginExpiredTable ||
      isBgvCompletedTable ||
      isReadyForBgcTable
    ) return false;
    if (typeof window !== 'undefined') {
      const role = String(localStorage.getItem('user_role') || localStorage.getItem('role') || '').toUpperCase();
      if (role === 'ADMIN' || window.location.pathname.startsWith('/admin') || window.location.pathname.startsWith('/client')) return false;
    }
    if (paginationMeta?.tableType === 'employee') return true;
    if (paginationMeta?.module === 'employee') return true;
    if (typeof window !== 'undefined') {
      const role = String(localStorage.getItem('user_role') || localStorage.getItem('role') || '').toUpperCase();
      if (role === 'EMPLOYEE' || window.location.pathname.includes('/employee')) {
        const title = String(paginationMeta?.title || '').toLowerCase();
        if (title.includes('assigned')) return true;
        return true;
      }
    }
    return false;
  }, [
    paginationMeta,
    isAdminClientTable,
    isAdminCandidateTable,
    isAdminEmployeeTable,
    isQuickBgvTable,
    isPendingSubmissionTable,
    isPendingCaseSubmissionTable,
    isSubmittedTable,
    isLoginExpiredTable,
    isBgvCompletedTable,
    isReadyForBgcTable
  ]);

  // Real-time column sort matching portal tables
  const sortedCandidates = useMemo(() => {
    if (!sortField) return filteredCandidates;
    return [...filteredCandidates].sort((a, b) => {
      let valA = a[sortField] ?? '';
      let valB = b[sortField] ?? '';
      if (sortField === 'status' || sortField === 'bgcStatus' || sortField === 'clientStatus') {
        valA = a.bgcStatus || a.status || a.clientStatus || '';
        valB = b.bgcStatus || b.status || b.clientStatus || '';
      } else if (sortField === 'mobile' || sortField === 'phone' || sortField === 'phonenumber' || sortField === 'mobileNumber') {
        valA = a.mobileNumber || a.mobile || a.phone || a.phoneNumber || '';
        valB = b.mobileNumber || b.mobile || b.phone || b.phoneNumber || '';
      } else if (sortField === 'name' || sortField === 'fullName') {
        valA = a.fullname || a.fullName || a.name || a.username || '';
        valB = b.fullname || b.fullName || b.name || b.username || '';
      } else if (sortField === 'id' || sortField === 'candidateId') {
        valA = a.candidateId || a.id || '';
        valB = b.candidateId || b.id || '';
      } else if (sortField === 'email' || sortField === 'candidateEmail') {
        valA = a.email || a.candidateEmail || '';
        valB = b.email || b.candidateEmail || '';
      } else if (sortField === 'createdDate') {
        valA = a.createdDate || a.caseInitiatedDateOnlyAsString || '';
        valB = b.createdDate || b.caseInitiatedDateOnlyAsString || '';
      } else if (sortField === 'dob' || sortField === 'dateOfBirth') {
        valA = a.dob || a.dateOfBirth || '';
        valB = b.dob || b.dateOfBirth || '';
      } else if (sortField === 'clientName' || sortField === 'company' || sortField === 'companyName') {
        valA = a.clientName || a.company || a.companyName || '';
        valB = b.clientName || b.company || b.companyName || '';
      } else if (sortField === 'completedDate' || sortField === 'bgvCompletedDate') {
        valA = a.completedDate || a.bgvCompletedDate || a.reportDateOnlyString || '';
        valB = b.completedDate || b.bgvCompletedDate || b.reportDateOnlyString || '';
      }
      const cmp = String(valA).localeCompare(String(valB), undefined, { numeric: true });
      return sortAsc ? cmp : -cmp;
    });
  }, [filteredCandidates, sortField, sortAsc]);

  const itemsPerPage = paginationMeta?.pageSize || 10;
  // For modal, use sorted & filtered candidates; for bubble markdown, use candidateItems
  const activeCandidates = modalOpen ? sortedCandidates : candidateItems;
  const totalCount = Math.max(activeCandidates.length, paginationMeta?.total || 0);
  const totalPages = Math.max(1, paginationMeta?.totalPages || Math.ceil(totalCount / itemsPerPage));

  // Reset page to 1 when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const safePage = Math.min(Math.max(1, currentPage), totalPages);
  const startIndex = (safePage - 1) * itemsPerPage;
  const currentItems = activeCandidates.length > itemsPerPage
    ? activeCandidates.slice(startIndex, startIndex + itemsPerPage)
    : activeCandidates;

  let activeMarkdown = content;
  if (paginationMeta && candidateItems.length > 0) {
    activeMarkdown = buildCandidateTable(
      currentItems,
      modalOpen ? (searchTerm ? filteredCandidates.length : (paginationMeta.total || filteredCandidates.length)) : (paginationMeta.total || candidateItems.length),
      startIndex,
      paginationMeta.title || 'candidates',
      {
        isAdminEmployee: isAdminEmployeeTable,
        isAdminCandidate: isAdminCandidateTable,
        isAdminClient: isAdminClientTable,
        isEmployee: isEmployeeTable,
        isQuickBgv: isQuickBgvTable,
        isPendingSubmission: isPendingSubmissionTable,
        isPendingCaseSubmission: isPendingCaseSubmissionTable,
        isSubmitted: isSubmittedTable,
        isLoginExpired: isLoginExpiredTable,
        isBgvCompleted: isBgvCompletedTable,
        isReadyForBgc: isReadyForBgcTable,
        tableType: paginationMeta?.tableType,
        module: paginationMeta?.module,
      }
    );
  } else if (content) {
    activeMarkdown = content.replace(/<!-- CANDIDATE_PAGINATION:[\s\S]*?-->/g, '').trim();
  }

  const formattedContent = formatContentLinks(activeMarkdown, isUser);

  const getTableColSpan = () => {
    if (isAdminCandidateTable || (typeof window !== 'undefined' && (window.location.pathname.startsWith('/admin') || localStorage.getItem('user_role') === 'ADMIN'))) {
      return 6;
    }
    if (isAdminClientTable || isAdminEmployeeTable || isEmployeeTable) {
      return 6;
    }
    if (isLoginExpiredTable) {
      return 7;
    }
    if (isBgvCompletedTable || isPendingCaseSubmissionTable || isQuickBgvTable || isPendingSubmissionTable) {
      return 8;
    }
    if (isReadyForBgcTable || isSubmittedTable) {
      return 9;
    }
    return 10;
  };

  // Enable expand icon only if response points exceed 5 (> 5 points)
  const pointCount = paginationMeta ? Math.min(itemsPerPage, candidateItems.length) : countResponsePoints(content);
  const shouldShowExpand = !isUser && pointCount > 5;

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
    if (activeCandidates.length <= itemsPerPage && (paginationMeta?.total || 0) > itemsPerPage && onSend) {
      onSend(`page ${newPage}`);
    }
  };

  const handleAction = async (actionType, candidateId) => {
    if (actionType === 'restore') {
      try {
        await restoreCandidate(candidateId);
        setActionToast({ type: 'success', message: `Login period extended for Candidate ${candidateId}.` });
        setCandidateItems((prev) =>
          prev.map((c) => (c.id === candidateId ? { ...c, loginExpired: false, actions: 'Active' } : c))
        );
      } catch (err) {
        setActionToast({ type: 'error', message: `Failed to restore login for ${candidateId}.` });
      }
    } else if (actionType === 'delete') {
      try {
        await softDeleteCandidate(candidateId);
        setActionToast({ type: 'success', message: `Candidate ${candidateId} deleted successfully.` });
        setCandidateItems((prev) => prev.filter((c) => c.id !== candidateId));
      } catch (err) {
        setActionToast({ type: 'error', message: `Failed to delete candidate ${candidateId}.` });
      }
    } else if (actionType === 'start_bgc') {
      try {
        await startCandidateBGC(candidateId);
        setActionToast({ type: 'success', message: `BGC verification process initiated for Candidate ${candidateId}.` });
        setCandidateItems((prev) => prev.filter((c) => c.id !== candidateId));
      } catch (err) {
        setActionToast({ type: 'error', message: `Failed to initiate BGC for ${candidateId}.` });
      }
    }
    setTimeout(() => setActionToast(null), 4000);
  };

  const renderPaginationControls = (isExpanded = false) => {
    const totalEntries = Math.max(activeCandidates.length, paginationMeta?.total || 0);
    if (!paginationMeta && totalEntries === 0) return null;
    if (!isExpanded && totalPages <= 1) return null;
    if (totalEntries === 0) return null;

    const startEntry = totalEntries > 0 ? (safePage - 1) * itemsPerPage + 1 : 0;
    const endEntry = Math.min(safePage * itemsPerPage, totalEntries);

    if (isExpanded) {
      const getPageNumbers = () => {
        if (totalPages <= 8) {
          return Array.from({ length: totalPages }, (_, i) => i + 1);
        }
        const pages = [];
        const delta = 2;
        const left = Math.max(1, safePage - delta);
        const right = Math.min(totalPages, safePage + delta);

        for (let i = left; i <= right; i++) {
          pages.push(i);
        }
        if (left > 2) {
          pages.unshift('...');
          pages.unshift(1);
        } else if (left === 2) {
          pages.unshift(1);
        }
        if (right < totalPages - 1) {
          pages.push('...');
          pages.push(totalPages);
        } else if (right === totalPages - 1) {
          pages.push(totalPages);
        }
        return pages;
      };

      return (
        <div className="chatbot-reference-pagination chatbot-reference-pagination--expanded">
          <div className="pagination-entries-info">
            Showing {startEntry} to {endEntry} of {totalEntries} entries
          </div>
          <div className="pagination-buttons">
            <button
              type="button"
              className="pagination-button"
              disabled={safePage <= 1}
              onClick={() => handlePageChange(safePage - 1)}
            >
              Previous
            </button>
            {getPageNumbers().map((p, idx) => (
              p === '...' ? (
                <span key={`dots-${idx}`} className="pagination-ellipsis" style={{ padding: '0 6px', color: '#64748b' }}>...</span>
              ) : (
                <button
                  key={p}
                  type="button"
                  className={`pagination-button ${safePage === p ? 'active' : ''}`}
                  onClick={() => handlePageChange(p)}
                >
                  {p}
                </button>
              )
            ))}
            <button
              type="button"
              className="pagination-button"
              disabled={safePage >= totalPages}
              onClick={() => handlePageChange(safePage + 1)}
            >
              Next
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="chatbot-reference-pagination chatbot-reference-pagination--bubble">
        <div className="pagination-entries-info">
          Showing <strong>{startEntry}-{endEntry}</strong> of <strong>{totalEntries}</strong>
        </div>
        <div className="pagination-controls-group">
          <button
            type="button"
            className="pagination-circle-btn"
            disabled={safePage <= 1}
            onClick={() => handlePageChange(1)}
            title="First page"
            aria-label="First page"
          >
            <ChevronsLeft size={15} />
          </button>
          <button
            type="button"
            className="pagination-circle-btn"
            disabled={safePage <= 1}
            onClick={() => handlePageChange(safePage - 1)}
            title="Previous page"
            aria-label="Previous page"
          >
            <ChevronLeft size={15} />
          </button>
          <span className="pagination-page-label">
            Page <strong>{safePage}</strong> of <strong>{totalPages}</strong>
          </span>
          <button
            type="button"
            className="pagination-circle-btn"
            disabled={safePage >= totalPages}
            onClick={() => handlePageChange(safePage + 1)}
            title="Next page"
            aria-label="Next page"
          >
            <ChevronRight size={15} />
          </button>
          <button
            type="button"
            className="pagination-circle-btn"
            disabled={safePage >= totalPages}
            onClick={() => handlePageChange(totalPages)}
            title="Last page"
            aria-label="Last page"
          >
            <ChevronsRight size={15} />
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className={`chatbot-message-row ${isUser ? 'chatbot-message-row--user' : 'chatbot-message-row--bot'}`}>
        {!isUser && (
          <div className="chatbot-avatar">
            <img
              src={logo || `${process.env.PUBLIC_URL || ''}/Trueface.png`}
              alt=""
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = `${process.env.PUBLIC_URL || ''}/Trueface.png`;
              }}
            />
          </div>
        )}
        <div className={`chatbot-bubble ${isUser ? 'chatbot-bubble--user' : 'chatbot-bubble--bot'} ${isWelcome ? 'chatbot-bubble--welcome' : ''} ${shouldShowExpand ? 'has-table-bubble' : ''}`}>
          {shouldShowExpand && (
            <button
              className="chatbot-table-expand-btn"
              title={`Expand view (${pointCount} points)`}
              onClick={() => setModalOpen(true)}
              aria-label="Expand detailed view to left side"
            >
              <Maximize2 size={13} />
            </button>
          )}
          {isUser ? (
            <span>{content}</span>
          ) : (
            <>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  a: ({ href, children }) => {
                    const isInternal = href && href.startsWith('/');
                    if (!isInternal) {
                      return <span>{children}</span>;
                    }
                    return (
                      <a
                        href={href}
                        className="chatbot-link-btn"
                        target="_self"
                        onClick={(e) => {
                          e.preventDefault();
                          window.history.pushState({}, '', href);
                          window.dispatchEvent(new Event('popstate'));
                          if (window.location.pathname !== href) {
                            setTimeout(() => {
                              if (window.location.pathname !== href) {
                                window.location.pathname = href;
                              }
                            }, 100);
                          }
                        }}
                      >
                        {children}
                      </a>
                    );
                  },
                }}
              >
                {formattedContent}
              </ReactMarkdown>
              {renderPaginationControls(false)}
            </>
          )}
        </div>
      </div>

      {/* 
        Expanded Table Drawer Portal — Mounts to document.body so it escapes 
        the chatbot-panel container and expands directly to the LEFT of the chatbot!
      */}
      {modalOpen &&
        ReactDOM.createPortal(
          <div className="chatbot-query-expand-overlay" onClick={() => setModalOpen(false)}>
            <div className="chatbot-query-expand-card chatbot-query-expand-card--wide" onClick={(e) => e.stopPropagation()}>
              <div className="chatbot-query-expand-header">
                <span className="chatbot-query-expand-title">Detailed Response View</span>
                <button
                  className="chatbot-query-expand-close"
                  onClick={() => setModalOpen(false)}
                  title="Close"
                  aria-label="Close detailed view"
                >
                  <X size={16} />
                </button>
              </div>

              {actionToast && (
                <div className={`chatbot-action-toast-banner chatbot-action-toast-banner--${actionToast.type}`}>
                  {actionToast.type === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
                  <span>{actionToast.message}</span>
                </div>
              )}

              <div className="chatbot-query-expand-body">
                {paginationMeta && candidateItems.length > 0 ? (
                  <div className="chatbot-interactive-roster">
                    <div className="chatbot-roster-top-bar">
                      <div className="chatbot-roster-count-text">
                        There are <strong>{searchTerm ? filteredCandidates.length : (paginationMeta.total || filteredCandidates.length)}</strong> {paginationMeta.title || 'candidates'}:
                      </div>
                      <div className="client-search-box chatbot-search-box">
                        <div className="client-search-input-wrapper">
                          <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search by any field..."
                            autoFocus
                          />
                          <Search className="client-search-icon" size={16} />
                        </div>
                        {searchTerm && (
                          <button
                            type="button"
                            className="chatbot-search-clear-btn"
                            onClick={() => setSearchTerm('')}
                            title="Clear search"
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="chatbot-table-scroll-container">
                      <table className="candidates-client-table chatbot-styled-table">
                        {isAdminCandidateTable || (typeof window !== 'undefined' && (window.location.pathname.startsWith('/admin') || String(localStorage.getItem('user_role') || '').toUpperCase() === 'ADMIN') && !isAdminClientTable && !isAdminEmployeeTable) ? (
                          <thead>
                            <tr>
                              <th onClick={() => handleSort('candidateId')}>Candidate ID {renderSortArrow('candidateId')}</th>
                              <th onClick={() => handleSort('fullName')}>Name {renderSortArrow('fullName')}</th>
                              <th onClick={() => handleSort('email')}>Email {renderSortArrow('email')}</th>
                              <th onClick={() => handleSort('bgcStatus')}>BGC Status {renderSortArrow('bgcStatus')}</th>
                              <th onClick={() => handleSort('clientName')}>Client Name {renderSortArrow('clientName')}</th>
                              <th style={{ textAlign: 'center' }}>Actions</th>
                            </tr>
                          </thead>
                        ) : isAdminEmployeeTable ? (
                          <thead>
                            <tr>
                              <th onClick={() => handleSort('id')}>Agent ID {renderSortArrow('id')}</th>
                              <th onClick={() => handleSort('name')}>Name {renderSortArrow('name')}</th>
                              <th onClick={() => handleSort('email')}>Email {renderSortArrow('email')}</th>
                              <th onClick={() => handleSort('phone')}>Contact Number {renderSortArrow('phone')}</th>
                              <th onClick={() => handleSort('status')}>Status {renderSortArrow('status')}</th>
                              <th style={{ textAlign: 'center' }}>Actions</th>
                            </tr>
                          </thead>
                        ) : isAdminClientTable ? (
                          <thead>
                            <tr>
                              <th onClick={() => handleSort('name')}>Client Name {renderSortArrow('name')}</th>
                              <th onClick={() => handleSort('company')}>Company {renderSortArrow('company')}</th>
                              <th onClick={() => handleSort('email')}>Email {renderSortArrow('email')}</th>
                              <th onClick={() => handleSort('phone')}>Contact Number {renderSortArrow('phone')}</th>
                              <th onClick={() => handleSort('status')}>Status {renderSortArrow('status')}</th>
                              <th style={{ textAlign: 'center' }}>Actions</th>
                            </tr>
                          </thead>
                        ) : isPendingCaseSubmissionTable ? (
                          <thead>
                            <tr>
                              <th onClick={() => handleSort('id')}>Candidate ID {renderSortArrow('id')}</th>
                              <th onClick={() => handleSort('name')}>Name {renderSortArrow('name')}</th>
                              <th onClick={() => handleSort('email')}>Email {renderSortArrow('email')}</th>
                              <th onClick={() => handleSort('dob')}>DOB {renderSortArrow('dob')}</th>
                              <th onClick={() => handleSort('createdDate')}>Created Date {renderSortArrow('createdDate')}</th>
                              <th>Created Time</th>
                              <th>Action</th>
                              <th style={{ textAlign: 'center' }}>View Details</th>
                            </tr>
                          </thead>
                        ) : isLoginExpiredTable ? (
                          <thead>
                            <tr>
                              <th onClick={() => handleSort('id')}>Candidate ID {renderSortArrow('id')}</th>
                              <th onClick={() => handleSort('name')}>Name {renderSortArrow('name')}</th>
                              <th onClick={() => handleSort('email')}>Email {renderSortArrow('email')}</th>
                              <th onClick={() => handleSort('mobile')}>Mobile Number {renderSortArrow('mobile')}</th>
                              <th onClick={() => handleSort('dob')}>DOB {renderSortArrow('dob')}</th>
                              <th onClick={() => handleSort('createdDate')}>Created Date {renderSortArrow('createdDate')}</th>
                              <th>Created Time</th>
                            </tr>
                          </thead>
                        ) : isBgvCompletedTable ? (
                          <thead>
                            <tr>
                              <th onClick={() => handleSort('id')}>Candidate ID {renderSortArrow('id')}</th>
                              <th onClick={() => handleSort('name')}>Name {renderSortArrow('name')}</th>
                              <th onClick={() => handleSort('email')}>Email {renderSortArrow('email')}</th>
                              <th onClick={() => handleSort('mobile')}>Mobile {renderSortArrow('mobile')}</th>
                              <th onClick={() => handleSort('dob')}>DOB {renderSortArrow('dob')}</th>
                              <th onClick={() => handleSort('createdDate')}>Created Date {renderSortArrow('createdDate')}</th>
                              <th onClick={() => handleSort('completedDate')}>BGV Completed Date {renderSortArrow('completedDate')}</th>
                              <th style={{ textAlign: 'center' }}>Action</th>
                            </tr>
                          </thead>
                        ) : isReadyForBgcTable ? (
                          <thead>
                            <tr>
                              <th onClick={() => handleSort('id')}>Candidate ID {renderSortArrow('id')}</th>
                              <th onClick={() => handleSort('name')}>Name {renderSortArrow('name')}</th>
                              <th onClick={() => handleSort('email')}>Email {renderSortArrow('email')}</th>
                              <th onClick={() => handleSort('mobile')}>Mobile Number {renderSortArrow('mobile')}</th>
                              <th onClick={() => handleSort('dob')}>DOB {renderSortArrow('dob')}</th>
                              <th onClick={() => handleSort('createdDate')}>Created Date {renderSortArrow('createdDate')}</th>
                              <th>Created Time</th>
                              <th onClick={() => handleSort('status')}>Status {renderSortArrow('status')}</th>
                              <th style={{ textAlign: 'center' }}>Actions</th>
                            </tr>
                          </thead>
                        ) : isSubmittedTable ? (
                          <thead>
                            <tr>
                              <th onClick={() => handleSort('id')}>Candidate ID {renderSortArrow('id')}</th>
                              <th onClick={() => handleSort('name')}>Name {renderSortArrow('name')}</th>
                              <th onClick={() => handleSort('email')}>Email {renderSortArrow('email')}</th>
                              <th onClick={() => handleSort('mobile')}>Mobile Number {renderSortArrow('mobile')}</th>
                              <th onClick={() => handleSort('dob')}>DOB {renderSortArrow('dob')}</th>
                              <th onClick={() => handleSort('createdDate')}>Created Date {renderSortArrow('createdDate')}</th>
                              <th>Created Time</th>
                              <th onClick={() => handleSort('status')}>Status {renderSortArrow('status')}</th>
                              <th style={{ textAlign: 'center' }}>Actions</th>
                            </tr>
                          </thead>
                        ) : isQuickBgvTable ? (
                          <thead>
                            <tr>
                              <th onClick={() => handleSort('id')}>Candidate ID {renderSortArrow('id')}</th>
                              <th onClick={() => handleSort('name')}>Full Name {renderSortArrow('name')}</th>
                              <th onClick={() => handleSort('email')}>Email {renderSortArrow('email')}</th>
                              <th onClick={() => handleSort('mobile')}>Mobile {renderSortArrow('mobile')}</th>
                              <th onClick={() => handleSort('dob')}>DOB {renderSortArrow('dob')}</th>
                              <th onClick={() => handleSort('createdBy')}>Created By {renderSortArrow('createdBy')}</th>
                              <th>Verification</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                        ) : isPendingSubmissionTable ? (
                          <thead>
                            <tr>
                              <th onClick={() => handleSort('id')}>Candidate ID {renderSortArrow('id')}</th>
                              <th onClick={() => handleSort('name')}>Name {renderSortArrow('name')}</th>
                              <th onClick={() => handleSort('email')}>Email {renderSortArrow('email')}</th>
                              <th onClick={() => handleSort('mobile')}>Mobile Number {renderSortArrow('mobile')}</th>
                              <th onClick={() => handleSort('dob')}>DOB {renderSortArrow('dob')}</th>
                              <th onClick={() => handleSort('createdDate')}>Created Date {renderSortArrow('createdDate')}</th>
                              <th onClick={() => handleSort('createdTime')}>Created Time {renderSortArrow('createdTime')}</th>
                              <th style={{ textAlign: 'center' }}>Actions</th>
                            </tr>
                          </thead>
                        ) : isEmployeeTable ? (
                          <thead>
                            <tr>
                              <th onClick={() => handleSort('id')}>Candidate ID {renderSortArrow('id')}</th>
                              <th onClick={() => handleSort('name')}>Name {renderSortArrow('name')}</th>
                              <th onClick={() => handleSort('email')}>Email {renderSortArrow('email')}</th>
                              <th onClick={() => handleSort('status')}>Status {renderSortArrow('status')}</th>
                              <th>Verification</th>
                              <th style={{ textAlign: 'center' }}>Actions</th>
                            </tr>
                          </thead>
                        ) : (typeof window !== 'undefined' && (window.location.pathname.startsWith('/admin') || localStorage.getItem('user_role') === 'ADMIN')) ? (
                          <thead>
                            <tr>
                              <th onClick={() => handleSort('candidateId')}>Candidate ID {renderSortArrow('candidateId')}</th>
                              <th onClick={() => handleSort('fullName')}>Name {renderSortArrow('fullName')}</th>
                              <th onClick={() => handleSort('email')}>Email {renderSortArrow('email')}</th>
                              <th onClick={() => handleSort('bgcStatus')}>BGC Status {renderSortArrow('bgcStatus')}</th>
                              <th onClick={() => handleSort('clientName')}>Client Name {renderSortArrow('clientName')}</th>
                              <th style={{ textAlign: 'center' }}>Actions</th>
                            </tr>
                          </thead>
                        ) : (
                          <thead>
                            <tr>
                              <th onClick={() => handleSort('id')}>Candidate ID {renderSortArrow('id')}</th>
                              <th onClick={() => handleSort('name')}>Name {renderSortArrow('name')}</th>
                              <th onClick={() => handleSort('email')}>Email {renderSortArrow('email')}</th>
                              <th onClick={() => handleSort('mobile')}>Mobile {renderSortArrow('mobile')}</th>
                              <th onClick={() => handleSort('dob')}>DOB {renderSortArrow('dob')}</th>
                              <th onClick={() => handleSort('createdDate')}>Created Date {renderSortArrow('createdDate')}</th>
                              <th onClick={() => handleSort('bgcStartDate')}>BGC Start Date {renderSortArrow('bgcStartDate')}</th>
                              <th onClick={() => handleSort('completedDate')}>Completed Date {renderSortArrow('completedDate')}</th>
                              <th onClick={() => handleSort('bgcStatus')}>Status {renderSortArrow('bgcStatus')}</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                        )}
                        <tbody>
                          {currentItems.length > 0 ? (
                            isAdminCandidateTable || (typeof window !== 'undefined' && (window.location.pathname.startsWith('/admin') || String(localStorage.getItem('user_role') || '').toUpperCase() === 'ADMIN') && !isAdminClientTable && !isAdminEmployeeTable) ? (
                              currentItems.map((c, index) => {
                                const cid = c.candidateId || c.id || '—';
                                const name = c.fullName || c.name || c.candidateName || '—';
                                const email = c.email || c.candidateEmail || c.emailId || c.userEmail || '—';
                                const statusRaw = c.bgcStatus || c.status || 'READY_FOR_BGC';
                                const statusClass = String(statusRaw).toLowerCase().replace(/_/g, '-');
                                const clientName = c.clientName || c.company || c.companyName || '—';
                                return (
                                  <tr key={cid || index}>
                                    <td><span className="chatbot-cell-id">{cid}</span></td>
                                    <td><strong>{name}</strong></td>
                                    <td className="chatbot-cell-email" title={email}>{email}</td>
                                    <td>
                                      <span className={`status-tag ${statusClass}`}>
                                        {statusRaw}
                                      </span>
                                    </td>
                                    <td>{clientName}</td>
                                    <td style={{ textAlign: 'center' }}>
                                      <button
                                        type="button"
                                        className="chatbot-table-view-btn"
                                        title="View Candidate Details"
                                        onClick={() => handleNavigate(`/admin/candidate-details/${cid}`)}
                                      >
                                        <Eye size={18} color="#4b0051" />
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })
                            ) : isAdminEmployeeTable ? (
                              currentItems.map((e, index) => {
                                const id = e.id || e.userid || e.userId || index;
                                const name = e.name || e.username || e.fullName || '—';
                                const email = e.email || '—';
                                const phone = e.phone || e.contact || e.phonenumber || '—';
                                const status = e.status || e.userStatus || 'Active';
                                const isActive = String(status).toUpperCase() === 'ACTIVE';
                                return (
                                  <tr key={id}>
                                    <td><span className="chatbot-cell-id">{id}</span></td>
                                    <td><strong>{name}</strong></td>
                                    <td className="chatbot-cell-email" title={email}>{email}</td>
                                    <td>{phone}</td>
                                    <td>
                                      <span className={`status-badge ${isActive ? 'status-active' : 'status-inactive'}`}>
                                        {status}
                                      </span>
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                      <div className="action-buttons-group" style={{ justifyContent: 'center' }}>
                                        <button
                                          type="button"
                                          className="candidate-btn"
                                          title="View Performance"
                                          onClick={() => handleNavigate(`/admin/employee-performance/${id}`)}
                                        >
                                          <FileBarChart2 size={16} />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })
                            ) : isAdminClientTable ? (
                              currentItems.map((c, index) => {
                                const id = c.id || c.clientId || index;
                                const name = c.name || c.username || c.clientName || '—';
                                const company = c.company || c.companyName || c.companyname || '—';
                                const email = c.email || c.emailaddress || c.companyEmail || '—';
                                const phone = c.phone || c.phoneNumber || c.phonenumber || '—';
                                const status = c.status || c.clientStatus || c.userStatus || 'ACTIVE';
                                const isActive = String(status).toUpperCase() === 'ACTIVE' || String(status).toUpperCase() === 'APPROVED';
                                return (
                                  <tr key={id}>
                                    <td><strong>{name}</strong></td>
                                    <td>{company}</td>
                                    <td className="chatbot-cell-email" title={email}>{email}</td>
                                    <td>{phone}</td>
                                    <td>
                                      <span className={`status-badge ${isActive ? 'status-active' : 'status-inactive'}`}>
                                        {status}
                                      </span>
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                      <div className="action-buttons-group" style={{ justifyContent: 'center' }}>
                                        <button
                                          type="button"
                                          className="candidate-btn"
                                          title="View Candidates"
                                          onClick={() => handleNavigate(`/admin/client/candidates/${id}`)}
                                        >
                                          <Users size={16} />
                                        </button>
                                        <button
                                          type="button"
                                          className="candidate-btn"
                                          style={{ backgroundColor: '#4f46e5', color: '#ffffff' }}
                                          title="Client Usage Report"
                                          onClick={() => handleNavigate(`/admin/reports/client-usage/${id}`)}
                                        >
                                          <FileBarChart2 size={16} />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })
                            ) : isPendingCaseSubmissionTable ? (
                              currentItems.map((c) => (
                                <tr key={c.id}>
                                  <td><span className="chatbot-cell-id">{c.id}</span></td>
                                  <td><strong>{c.name}</strong></td>
                                  <td className="chatbot-cell-email" title={c.email}>{c.email || '—'}</td>
                                  <td>{c.dob || '—'}</td>
                                  <td>{c.createdDate || '---'}</td>
                                  <td>{c.createdTime || '---'}</td>
                                  <td>
                                    <button
                                      type="button"
                                      className="bgc-request-btn"
                                      onClick={() => handleAction('start_bgc', c.id)}
                                    >
                                      Request for BGC
                                    </button>
                                  </td>
                                  <td style={{ textAlign: 'center' }}>
                                    <button
                                      type="button"
                                      className="chatbot-table-view-btn"
                                      title="View Report"
                                      onClick={() => handleNavigate(`/candidate-details/${c.id}`)}
                                    >
                                      <Eye size={18} color="#4b0051" />
                                    </button>
                                  </td>
                                </tr>
                              ))
                            ) : isLoginExpiredTable ? (
                              currentItems.map((c) => (
                                <tr key={c.id}>
                                  <td><span className="chatbot-cell-id">{c.id}</span></td>
                                  <td><strong>{c.name}</strong></td>
                                  <td className="chatbot-cell-email" title={c.email}>{c.email || '—'}</td>
                                  <td>{c.mobile || c.mobileNumber || '—'}</td>
                                  <td>{c.dob || '—'}</td>
                                  <td>{c.createdDate || '---'}</td>
                                  <td>{c.createdTime || '---'}</td>
                                </tr>
                              ))
                            ) : isBgvCompletedTable ? (
                              currentItems.map((c) => {
                                const isCompleted = String(c.bgcStatus || c.status || 'COMPLETED').toUpperCase() === 'COMPLETED';
                                const compDate = c.completedDate || c.bgvCompletedDate || c.reportDateOnlyString || '---';
                                return (
                                  <tr key={c.id}>
                                    <td><span className="chatbot-cell-id">{c.id}</span></td>
                                    <td><strong>{c.name}</strong></td>
                                    <td className="chatbot-cell-email" title={c.email}>{c.email || '—'}</td>
                                    <td>{c.mobile || c.mobileNumber || '—'}</td>
                                    <td>{c.dob || '—'}</td>
                                    <td>{c.createdDate || '---'}</td>
                                    <td>{compDate}</td>
                                    <td style={{ textAlign: 'center' }}>
                                      <button
                                        type="button"
                                        className={`chatbot-icon-btn ${!isCompleted ? 'disabled' : ''}`}
                                        title={isCompleted ? 'View Final Candidate Report' : 'Report incomplete'}
                                        disabled={!isCompleted}
                                        onClick={() => handleNavigate(`/client/finalreport/${c.id}`)}
                                        style={{ color: '#6b21a8' }}
                                      >
                                        <FileText size={17} />
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })
                            ) : isReadyForBgcTable ? (
                              currentItems.map((c, index) => {
                                const cid = c.candidateId || c.id || '—';
                                const name = c.fullName || c.name || c.candidateName || '—';
                                const email = c.email || c.candidateEmail || '—';
                                const mobile = c.mobileNumber || c.mobile || '—';
                                const dob = c.dob || c.dateOfBirth || '—';
                                const cDate = c.createdDate || c.caseInitiatedDateOnly || '---';
                                const cTime = c.createdTime || c.caseInitiatedTimeOnly || '---';
                                const rawStatus = c.status || c.bgcStatus || 'READY_FOR_BGC';
                                return (
                                  <tr key={cid || index}>
                                    <td><span className="chatbot-cell-id">{cid}</span></td>
                                    <td><strong>{name}</strong></td>
                                    <td className="chatbot-cell-email" title={email}>{email}</td>
                                    <td>{mobile}</td>
                                    <td>{dob}</td>
                                    <td>{cDate}</td>
                                    <td>{cTime}</td>
                                    <td>
                                      <span className="status-tag ready-for-bgc">
                                        {rawStatus}
                                      </span>
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                      <button
                                        type="button"
                                        className="chatbot-table-view-btn"
                                        title="View Details"
                                        onClick={() => handleNavigate(`/candidate-details/${cid}`)}
                                      >
                                        <Eye size={18} color="#4b0051" />
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })
                            ) : isSubmittedTable ? (
                              currentItems.map((c) => {
                                const statusRaw = c.status || c.bgcStatus || 'WORK_IN_PROGRESS';
                                const statusClass = String(statusRaw).toLowerCase().replace(/_/g, '-');
                                return (
                                  <tr key={c.id}>
                                    <td><span className="chatbot-cell-id">{c.id}</span></td>
                                    <td><strong>{c.name}</strong></td>
                                    <td className="chatbot-cell-email" title={c.email}>{c.email || '—'}</td>
                                    <td>{c.mobile || c.mobileNumber || '—'}</td>
                                    <td>{c.dob || '—'}</td>
                                    <td>{c.createdDate || '---'}</td>
                                    <td>{c.createdTime || '---'}</td>
                                    <td>
                                      <span className={`status-tag ${statusClass}`}>
                                        {statusRaw}
                                      </span>
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                      <button
                                        type="button"
                                        className="chatbot-table-view-btn"
                                        title="View Report"
                                        onClick={() => handleNavigate(`/candidate-details/${c.id}`)}
                                      >
                                        <Eye size={18} color="#4b0051" />
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })
                            ) : isQuickBgvTable ? (
                              currentItems.map((c) => {
                                const statusRaw = c.status || c.bgcStatus || 'IN PROGRESS';
                                const statusClass = getEmployeeStatusClass(statusRaw);
                                return (
                                  <tr key={c.id}>
                                    <td><span className="chatbot-cell-id">{c.id}</span></td>
                                    <td><strong>{c.name}</strong></td>
                                    <td className="chatbot-cell-email" title={c.email}>{c.email || '—'}</td>
                                    <td>{c.mobile || c.mobileNumber || '—'}</td>
                                    <td>{c.dob || '—'}</td>
                                    <td>{c.createdBy || '—'}</td>
                                    <td className="text-center align-middle" style={{ textAlign: 'center' }}>
                                      <button
                                        type="button"
                                        className="start-bgc-button"
                                        onClick={() => handleNavigate(`/employee/quickbgv-dashboard/${c.id}`)}
                                      >
                                        Start BGC
                                      </button>
                                    </td>
                                    <td>
                                      <span className={`assigned-status-badge ${statusClass}`}>
                                        {statusRaw}
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })
                            ) : isPendingSubmissionTable ? (
                              currentItems.map((c) => {
                                const isExp = c.loginExpired || c.actions === 'Login Expired';
                                return (
                                  <tr key={c.id}>
                                    <td><span className="chatbot-cell-id">{c.id}</span></td>
                                    <td><strong>{c.name}</strong></td>
                                    <td className="chatbot-cell-email" title={c.email}>{c.email || '—'}</td>
                                    <td>{c.mobile || c.mobileNumber || '—'}</td>
                                    <td>{c.dob || '—'}</td>
                                    <td>{c.createdDate || '---'}</td>
                                    <td>{c.createdTime || '---'}</td>
                                    <td className="chatbot-cell-actions" style={{ justifyContent: 'center' }}>
                                      <span
                                        className="action-icon recycle"
                                        title="Restore Login"
                                        style={{ color: '#6b21a8', cursor: 'pointer' }}
                                        onClick={() => handleAction('restore', c.id)}
                                      >
                                        <RotateCcw size={18} />
                                      </span>

                                      {isExp ? (
                                        <span className="login-expired-badge">Login Expired</span>
                                      ) : (
                                        <span className="active-badge">Active</span>
                                      )}

                                      <span
                                        className="action-icon delete"
                                        title="Delete"
                                        style={{ color: '#dc2626', cursor: 'pointer', marginLeft: '6px' }}
                                        onClick={() => handleAction('delete', c.id)}
                                      >
                                        <Trash2 size={18} />
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })
                            ) : isEmployeeTable ? (
                              currentItems.map((c) => {
                                const statusRaw = c.status || c.bgcStatus || 'WORK_IN_PROGRESS';
                                const statusClass = getEmployeeStatusClass(statusRaw);
                                const bgcLabel = getBGCButtonLabel(statusRaw);
                                const bgcDisabled = isBGCButtonDisabled(statusRaw);
                                return (
                                  <tr key={c.id}>
                                    <td><span className="chatbot-cell-id">{c.id}</span></td>
                                    <td><strong>{c.name}</strong></td>
                                    <td className="chatbot-cell-email" title={c.email}>{c.email || '—'}</td>
                                    <td>
                                      <span className={`assigned-status-badge ${statusClass}`}>
                                        {statusRaw}
                                      </span>
                                    </td>
                                    <td className="text-center align-middle" style={{ textAlign: 'center' }}>
                                      <button
                                        type="button"
                                        className="start-bgc-button"
                                        disabled={bgcDisabled}
                                        onClick={() => handleNavigate(`/employee/candidateDetails/${c.id}`)}
                                      >
                                        {bgcLabel}
                                      </button>
                                    </td>
                                    <td className="chatbot-cell-actions" style={{ justifyContent: 'center' }}>
                                      <div className="action-buttons-group">
                                        <button
                                          type="button"
                                          className="action-button view-button"
                                          title="View Details"
                                          onClick={() => handleNavigate(`/candidate-details/${c.id}`)}
                                        >
                                          <Eye size={17} color="#4D0054" />
                                        </button>
                                        <button
                                          type="button"
                                          className="action-button mail-button"
                                          title="Send Mail / Verify"
                                          onClick={() => {
                                            setActionToast({ type: 'success', message: `Verification notice for Candidate ${c.id} sent.` });
                                            setTimeout(() => setActionToast(null), 3000);
                                          }}
                                        >
                                          <Mail size={17} color="#4D0054" />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })
                            ) : (typeof window !== 'undefined' && (window.location.pathname.startsWith('/admin') || localStorage.getItem('user_role') === 'ADMIN')) ? (
                              currentItems.map((c, index) => {
                                const cid = c.candidateId || c.id || '—';
                                const name = c.fullName || c.name || c.candidateName || '—';
                                const email = c.email || c.candidateEmail || c.emailId || c.userEmail || '—';
                                const statusRaw = c.bgcStatus || c.status || 'VERIFIED';
                                const statusClass = String(statusRaw).toLowerCase().replace(/_/g, '-');
                                const clientName = c.clientName || c.company || c.companyName || '—';
                                return (
                                  <tr key={cid || index}>
                                    <td><span className="chatbot-cell-id">{cid}</span></td>
                                    <td><strong>{name}</strong></td>
                                    <td className="chatbot-cell-email" title={email}>{email}</td>
                                    <td>
                                      <span className={`status-tag ${statusClass}`}>
                                        {statusRaw}
                                      </span>
                                    </td>
                                    <td>{clientName}</td>
                                    <td style={{ textAlign: 'center' }}>
                                      <button
                                        type="button"
                                        className="chatbot-table-view-btn"
                                        title="View Candidate Details"
                                        onClick={() => handleNavigate(`/admin/candidate-details/${cid}`)}
                                      >
                                        <Eye size={18} color="#4b0051" />
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })
                            ) : (
                              currentItems.map((c) => {
                                const isCompleted = String(c.bgcStatus || c.status || '').toUpperCase() === 'COMPLETED';
                                const isPending = String(c.bgcStatus || c.status || '').toUpperCase() === 'PENDING_SUBMISSION';
                                const statusRaw = c.bgcStatus || c.status || '—';
                                const statusClass = String(statusRaw).toLowerCase().replace(/_/g, '-');
                                const isExp = c.loginExpired || c.actions === 'Login Expired';
                                return (
                                  <tr key={c.id}>
                                    <td><span className="chatbot-cell-id">{c.id}</span></td>
                                    <td><strong>{c.name}</strong></td>
                                    <td className="chatbot-cell-email" title={c.email}>{c.email || '—'}</td>
                                    <td>{c.mobile || c.mobileNumber || '—'}</td>
                                    <td>{c.dob || '—'}</td>
                                    <td>{c.createdDate || '---'}</td>
                                    <td>{c.bgcStartDate || '---'}</td>
                                    <td>{c.completedDate || '---'}</td>
                                    <td>
                                      <span className={`status-tag ${statusClass}`}>
                                        {statusRaw}
                                      </span>
                                    </td>
                                    <td className="chatbot-cell-actions">
                                      <div className="chatbot-table-action-btns">
                                        <button
                                          type="button"
                                          className={`chatbot-icon-btn ${isPending ? 'disabled' : ''}`}
                                          title={isPending ? 'Report not available' : 'View Report'}
                                          disabled={isPending}
                                          onClick={() => handleNavigate(`/candidate-details/${c.id}`)}
                                        >
                                          <Eye size={17} />
                                        </button>
                                        <button
                                          type="button"
                                          className={`chatbot-icon-btn ${!isCompleted ? 'disabled' : ''}`}
                                          title={isCompleted ? 'View Final Candidate Report' : 'Report incomplete'}
                                          disabled={!isCompleted}
                                          onClick={() => handleNavigate(`/client/finalreport/${c.id}`)}
                                        >
                                          <FileText size={17} />
                                        </button>
                                        {isExp && (
                                          <button
                                            type="button"
                                            className="chatbot-icon-btn recycle"
                                            title="Restore Login"
                                            onClick={() => handleAction('restore', c.id)}
                                          >
                                            <RotateCcw size={17} />
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })
                            )
                          ) : (
                            <tr>
                              <td colSpan={getTableColSpan()} className="no-data">
                                No matching candidates found for "{searchTerm}".
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {renderPaginationControls(true)}
                  </div>
                ) : (
                  <>
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        a: ({ href, children }) => {
                          const isInternal = href && href.startsWith('/');
                          if (!isInternal) return <span>{children}</span>;
                          return (
                            <a href={href} className="chatbot-link-btn" target="_self">
                              {children}
                            </a>
                          );
                        },
                      }}
                    >
                      {formattedContent}
                    </ReactMarkdown>
                    {renderPaginationControls(true)}
                  </>
                )}
              </div>
            </div>
          </div>,
          document.body
        )
      }
    </>
  );
};

export default MessageBubble;
